import { api } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import type { XkEventStockStation, XkStock } from '@/lib/api/types';

function assertNotDeleted<T extends { status?: string }>(entity: T, kind: string): T {
  if (entity?.status === 'Deleted') {
    throw new ApiError({ message: `${kind} has been deleted`, status: 404, code: 'NOT_FOUND' });
  }
  return entity;
}

/**
 * Stock & stock-station allocation API.
 *
 * Backend routes (ox-platform):
 *   GET    /xk-stock                                     → list
 *   GET    /xk-stock/:id                                 → single
 *   POST   /xk-stock                                     → create
 *   PUT    /xk-stock/:id                                 → update
 *   DELETE /xk-stock/:id                                 → delete
 *
 *   GET    /xk-event-stock-station                       → list allocations
 *   POST   /xk-event-stock-station                       → create allocation
 *   PUT    /xk-event-stock-station/:id                   → update
 *   DELETE /xk-event-stock-station/:id                   → delete
 *
 *   GET    /xk-event-stock-station/report/group/:id      → group report
 *   GET    /xk-event-stock-station/report/event/:id      → event report
 *   GET    /xk-event-stock-station/report/station/:id    → station report
 */

export interface StockReportItem {
  stockName: string;
  quantity: number;
  available: number;
  eventName: string;
  stationName: string;
}

export interface StockReport {
  items: StockReportItem[];
  totalQuantity: number;
  totalAvailable: number;
}

export const stockApi = {
  list: (filter?: Record<string, unknown>) => api.list<XkStock>('/xk-stock', { filter }),
  get: (id: number) =>
    api.get<XkStock>(`/xk-stock/${id}`).then((s) => assertNotDeleted(s, 'Stock')),
  create: (input: Partial<XkStock>) => api.post<XkStock>('/xk-stock', input),
  update: (id: number, patch: Partial<XkStock>) => api.put<XkStock>(`/xk-stock/${id}`, patch),
  // Soft delete via PUT — see eventsApi for the rationale. Listing endpoints
  // hide entities with status='Deleted'.
  remove: (id: number) => api.put<XkStock>(`/xk-stock/${id}`, { status: 'Deleted' }),
  // Soft-disable instead of delete: keeps allocation history & redemption
  // audit trail intact. Used by Brand Studio Prizes "Remove from brand".
  disable: (id: number) => api.put<XkStock>(`/xk-stock/${id}`, { status: 'Disable' }),
  enable: (id: number) => api.put<XkStock>(`/xk-stock/${id}`, { status: 'Enable' }),

  // PRD §9 — Agency master catalog (read for the Brand Studio Prizes tab).
  agencyCatalog: (agencyId: number) =>
    api.get<XkStock[]>(`/xk-stock/agency-catalog?agencyId=${agencyId}`),

  // PRD §9 — Brand adopts an agency master prize. Idempotent: re-call to
  // restock by `initialQuantity` rather than create a duplicate row.
  adopt: (agencyStockId: number, body: { brandId: number; initialQuantity: number }) =>
    api.post<XkStock>(`/xk-stock/${agencyStockId}/adopt`, body),

  // PRD §9 — Adjust a brand prize by `delta` units (positive = restock,
  // negative = write-off / correction).
  restock: (stockId: number, delta: number) =>
    api.patch<XkStock>(`/xk-stock/${stockId}/restock`, { delta }),
};

export const allocationsApi = {
  list: (filter?: Record<string, unknown>) =>
    api.list<XkEventStockStation>('/xk-event-stock-station', { filter }),
  get: (id: number) =>
    api
      .get<XkEventStockStation>(`/xk-event-stock-station/${id}`)
      .then((a) => assertNotDeleted(a, 'Allocation')),
  create: (input: Partial<XkEventStockStation>) =>
    api.post<XkEventStockStation>('/xk-event-stock-station', input),
  update: (id: number, patch: Partial<XkEventStockStation>) =>
    api.put<XkEventStockStation>(`/xk-event-stock-station/${id}`, patch),
  // Soft delete — see eventsApi.
  remove: (id: number) =>
    api.put<XkEventStockStation>(`/xk-event-stock-station/${id}`, { status: 'Deleted' }),

  reportByGroup: (eventGroupId: number) =>
    api.get<StockReport>(`/xk-event-stock-station/report/group/${eventGroupId}`),
  reportByEvent: (eventId: number) =>
    api.get<StockReport>(`/xk-event-stock-station/report/event/${eventId}`),
  reportByStation: (stationId: number) =>
    api.get<StockReport>(`/xk-event-stock-station/report/station/${stationId}`),
};
